import React, { lazy, Suspense, useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import SeoManager from "./components/SeoManager";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
const Home = lazy(() => import("./components/Home/Home"));
const Contact = lazy(() => import("./components/Contact/Contact"));
const Team = lazy(() => import("./components/Team/Team"));
const Registration = lazy(() => import("./components/Registration/Registration"));

const About = lazy(() => import("./components/About/About"));
const Story = lazy(() => import("./components/Story/Story"));
const Mission = lazy(() => import("./components/Mission/Mission"));
const Partners = lazy(() => import("./components/Partners/Partners"));
const Testimonial = lazy(() => import("./components/Testimonials/Testimonial"));

const Projects = lazy(() => import("./components/Projects/Projects"));
const AdmissionsPage = lazy(() => import("./components/Career/AdmissionsPage"));
const CatalogPage = lazy(() => import("./components/Career/CatalogPage"));
const StudentResourcesPage = lazy(() => import("./components/Career/StudentResourcesPage"));
const CareersPage = lazy(() => import("./components/Career/CareersPage"));
const CareerPositionPage = lazy(() => import("./components/Career/CareerPositionPage"));
const Tiers = lazy(() => import("./components/Tiers/Tiers"));
const TrainingsTiers = lazy(() => import("./components/TrainingsTiers/TrainingsTiers"));

const Favrhood = lazy(() => import("./components/Favrhood/Favrhood"));
const ConsultingAdvisory = lazy(() => import("./components/ConsultingAdvisory/ConsultingAdvisory"));
const Lab = lazy(() => import("./components/Lab/LabPage"));
const CampusToCommunityInitiative = lazy(() => import("./components/Lab/CampusToCommunityInitiative"));
const WebPricing = lazy(() => import("./components/WebPricing/PricingCard1"));
const RoboticsPricing = lazy(() => import("./components/RoboticsPricing/RoboticsCard"));
const ThreeDWebPricing = lazy(() => import("./components/ThreeDWebPricing/ThreeDWebCard"));
const ThreeDModelingPricing = lazy(() => import("./components/ThreeDModelingPricing/ThreeDModelingCard"));
const JusticeTechPricing = lazy(() => import("./components/JusticeTechPricing/JusticeTechCard"));
const AppropTechPricing = lazy(() => import("./components/AppropTechPricing/AppropTechCard"));

const Stats = lazy(() => import("./components/Stats/Stats"));
const ImpactHome = lazy(() => import("./components/Activities/ImpactHome"));
const Impact = lazy(() => import("./components/Impact/Impact"));
const HandsOnLearning = lazy(() => import("./components/HandsOnLearning/HandsOnLearning"));
const ExploratoryLearning = lazy(() => import("./components/ExploratoryLearning/ExploratoryLearning"));
const Workshop = lazy(() => import("./components/Workshops/Workshop"));
const Afterschool = lazy(() => import("./components/Afterschool/Afterschool"));
const Ambassadors = lazy(() => import("./components/Ambassadors/Ambassadors"));
const TechPal = lazy(() => import("./components/TechPal/TechPal"));
const Camps = lazy(() => import("./components/Camps/Camps"));
const Senior = lazy(() => import("./components/Seniors/Senior"));
const Neighborhood = lazy(() => import("./components/Neighborhood/Neighborhood"));
const PartnerWithUs = lazy(() => import("./components/PartnerWithUs/PartnerWithUs"));
const VolunteerOpportunities = lazy(() => import("./components/VolunteerOpportunities/VolunteerOpportunities"));
const InternExperience = lazy(() => import("./components/InternExperience/InternExperience"));
const Support = lazy(() => import("./components/Support/Support"));
const GrantsPage = lazy(() => import("./components/Grants/GrantsPage"));

const GrievanceAppealsPolicy = lazy(() => import("./components/Policies/GrievanceAppealsPolicy"));
const PrivacyPolicy = lazy(() => import("./components/Policies/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./components/Policies/TermsOfUse"));



function App() {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = window.localStorage.getItem("cf-theme");
      return saved === "light" || saved === "dark" ? saved : "light";
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      window.localStorage.setItem("cf-theme", theme);
    } catch {
      document.documentElement.setAttribute("data-theme", theme);
    }

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute("content", theme === "light" ? "#0b6f7d" : "#06161d");
    }
  }, [theme]);

  useEffect(() => {
    let rafId = null;
    let scheduled = false;
    let processedImageCount = 0;
    const pendingImages = new Set();

    const optimizeImages = () => {
      scheduled = false;
      const viewportHeight = window.innerHeight || 800;

      pendingImages.forEach((img) => {
        pendingImages.delete(img);

        if (!img.isConnected) {
          return;
        }

        if (!img.hasAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }

        const rect = img.getBoundingClientRect();
        const nearViewport = rect.top < viewportHeight * 1.25 && rect.bottom > -viewportHeight * 0.25;

        if (!img.hasAttribute("loading")) {
          const shouldEagerLoad = nearViewport && processedImageCount < 2;
          img.setAttribute("loading", shouldEagerLoad ? "eager" : "lazy");
        }

        if (!img.hasAttribute("fetchpriority")) {
          const isHeroCandidate = nearViewport && rect.top < viewportHeight * 0.9;
          if (isHeroCandidate && img.getAttribute("loading") === "eager") {
            img.setAttribute("fetchpriority", "high");
          } else if (img.getAttribute("loading") === "lazy") {
            img.setAttribute("fetchpriority", "low");
          }
        }
        processedImageCount += 1;
      });
    };

    const scheduleOptimization = (images) => {
      images.forEach((img) => pendingImages.add(img));

      if (scheduled) return;
      scheduled = true;
      rafId = window.requestAnimationFrame(optimizeImages);
    };

    scheduleOptimization(Array.from(document.images));

    const observer = new MutationObserver((mutations) => {
      const addedImages = [];

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches("img")) {
            addedImages.push(node);
          }
          addedImages.push(...node.querySelectorAll("img"));
        });
      });

      if (addedImages.length > 0) {
        scheduleOptimization(addedImages);
      }
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    const optimizeCurrentImages = () => scheduleOptimization(Array.from(document.images));
    window.addEventListener("resize", optimizeCurrentImages, { passive: true });
    window.addEventListener("load", optimizeCurrentImages, { once: true });

    return () => {
      observer.disconnect();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", optimizeCurrentImages);
      window.removeEventListener("load", optimizeCurrentImages);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  };

  return (
    <Router>
      <SeoManager />
      <div className="App" id="scroll">
        <Navbar theme={theme} onToggleTheme={toggleTheme} />
        <ScrollToTop />
        <Suspense fallback={<div className="route-loading" role="status" aria-label="Loading page" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Projects />} />
            <Route path="/admissions" element={<AdmissionsPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/student-resources" element={<StudentResourcesPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/careers/:slug" element={<CareerPositionPage />} />
            <Route path="/tiers" element={<Tiers />} />
            <Route path="/programs/trainings-tiers" element={<TrainingsTiers />} />
            <Route path="/about/who-we-are" element={<About />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/impact" element={<Stats />} />
            <Route path="/impact/community" element={<ImpactHome />} />
            <Route path="/team" element={<Team />} />
            <Route path="/about/testimonials" element={<Testimonial />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/web-pricing" element={<WebPricing />} />
            <Route path="/robotics-pricing" element={<RoboticsPricing />} />
            <Route path="/three-d-web-pricing" element={<ThreeDWebPricing />} />
            <Route path="/three-d-modeling-pricing" element={<ThreeDModelingPricing />} />
            <Route path="/justice-tech-pricing" element={<JusticeTechPricing />} />
            <Route path="/approp-tech-pricing" element={<AppropTechPricing />} />
            <Route path="/services/favrhood" element={<Favrhood />} />
            <Route path="/services/consulting-advisory" element={<ConsultingAdvisory />} />
            <Route path="/favrhood" element={<Navigate to="/services/favrhood" replace />} />
            <Route path="/projects" element={<Lab />} />
            <Route path="/projects/campus-to-community-innovation-initiative" element={<CampusToCommunityInitiative />} />
            <Route path="/lab" element={<Lab />} />
            <Route path="/programs/syep-internship" element={<Impact />} />
            <Route path="/programs/community-tech-programs" element={<HandsOnLearning />} />
            <Route path="/programs/hands-on-learning" element={<Navigate to="/programs/community-tech-programs" replace />} />
            <Route path="/programs/exploratory-learning" element={<ExploratoryLearning />} />
            <Route path="/programs/workshops" element={<Workshop />} />
            <Route path="/programs/after-school" element={<Afterschool />} />
            <Route path="/programs/ambassadors" element={<Ambassadors />} />
            <Route path="/programs/tech-pal" element={<TechPal />} />
            <Route path="/programs/partner-with-us" element={<PartnerWithUs />} />
            <Route path="/programs/volunteer-opportunities" element={<VolunteerOpportunities />} />
            <Route path="/grants" element={<GrantsPage />} />
            <Route path="/programs/tech-camps" element={<Camps />} />
            <Route path="/programs/senior-citizens" element={<Senior />} />
            <Route path="/programs/code-and-soil" element={<Neighborhood />} />
            <Route path="/support" element={<Support />} />
            <Route path="/about/our-story" element={<Story />} />
            <Route path="/about/our-partners" element={<Partners />} />
            <Route path="/about/our-mission" element={<Mission />} />
            <Route path="/programs/intern-experience" element={<InternExperience />} />
            <Route path="/about/impact" element={<Navigate to="/impact" replace />} />
            <Route path="/policies/grievance-appeals" element={<GrievanceAppealsPolicy />} />
            <Route path="/policies/privacy" element={<PrivacyPolicy />} />
            <Route path="/policies/terms" element={<TermsOfUse />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <Footer />
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
